import { GadgetLogoMark } from "@gadgetinc/widgets/src/icons/GadgetLogo";
import { useStyletron } from "baseui";
import React, { useEffect, useState } from "react";
import type { GadgetAppSummon } from "state-trees/src/assistant/summon";
import { AppSummonMessage, SummonMessage, SummonTextChunk } from "./AppSummonMessage";
import { useIsMobileOrSlimScreen } from "../../../../lib/useIsMobile";

export const EditBootloaderSkeleton = ({
  activeMaintenanceWindow,
  stageMessage,
  errorMessage,
  appSummon,
  fadeIn = false,
}: {
  activeMaintenanceWindow?: boolean;
  stageMessage?: string | null;
  errorMessage?: string;
  appSummon?: GadgetAppSummon;
  fadeIn?: boolean;
}) => {
  const { isMobile } = useIsMobileOrSlimScreen();

  return appSummon ? (
    <AppSummonBootLoader appSummon={appSummon} fadeIn={fadeIn} />
  ) : (
    <BootLoaderShell showLogo={!isMobile}>
      {!isMobile && <div className="GadgetBootLoader__left-nav"></div>}
      <div className="GadgetBootLoader__stage">
        <div className="GadgetBootLoader__spinner-container">
          <div className="loader"></div>
          <div className="loader-app">{window.GadgetEditConfig.startApplicationDomain}</div>
          <div className="loader-message">{stageMessage ?? "loading"}...</div>
          {activeMaintenanceWindow && (
            <div className="GadgetBootLoader__maintenance-window">
              <p>Your application is currently under active maintenance.</p>
              <p>If your app is still not loading within 10 seconds, try refreshing the window.</p>
              <p>If the issue persists, please contact support.</p>
            </div>
          )}
          {errorMessage && (
            <div className="GadgetBootLoader__error-message">
              <b>An unexpected error occurred while loading your application.</b>
              <p>{errorMessage}</p>
              <p>Retrying...</p>
            </div>
          )}
        </div>
      </div>
    </BootLoaderShell>
  );
};

export const AppSummonBootLoader = ({ appSummon, fadeIn = false }: { appSummon: GadgetAppSummon; fadeIn?: boolean }) => {
  return (
    <AppSummonBootLoaderShell
      fadeIn={fadeIn}
      prompt={appSummon.prompt}
      user={appSummon.userEmailHash}
      appSummary={
        <AppSummonMessage
          prompt={appSummon.prompt}
          summary={appSummon.summary}
          appName={appSummon.appName}
          appTemplateType={appSummon.appTemplateType}
          shopifyAppType={appSummon.shopifyAppType}
        />
      }
      settingUpMessage={appSummon.settingUpMessage}
      settingUpSteps={appSummon.settingUpSteps.map((step) => ({ step, completed: true }))}
      loading={true}
      showLogo
    />
  );
};

export const AppSummonBootLoaderShell = (props: {
  prompt?: string | null;
  user?: string | null;
  systemMessage?: string | null;
  appSummary?: React.ReactNode | null;
  fadeIn?: boolean;
  settingUpMessage?: string | null;
  settingUpSteps?: {
    step: string;
    details?: string | null;
    completed: boolean;
  }[];
  loading?: boolean;
  showLogo?: boolean;
}) => {
  const { fadeIn, prompt, user, systemMessage, appSummary, settingUpMessage, settingUpSteps, loading, showLogo } = props;
  const [opacity, setOpacity] = useState(props.fadeIn ? 0 : 1);

  useEffect(() => {
    if (fadeIn) {
      const timeout = setTimeout(() => {
        setOpacity(1);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [fadeIn]);

  return (
    <BootLoaderShell showLogo={showLogo}>
      <div className="Assistant__chat Assistant__summon-max-chat">
        <div className="Assistant__chat-section">
          <div
            className="Assistant__chat-container"
            style={{
              overflow: "auto",
              opacity,
              transition: fadeIn && opacity === 1 ? "opacity 0.3s ease-in" : "none",
            }}
          >
            {prompt && user && <SummonMessage user={user}>{appSummary}</SummonMessage>}
            {systemMessage && (
              <SummonMessage content={systemMessage}>
                {settingUpMessage && <SummonTextChunk content={settingUpMessage} />}
                {settingUpSteps && settingUpSteps.length > 0 && (
                  <>
                    {settingUpSteps.map((step, idx) => (
                      <SettingUpStep key={step.step} {...step} />
                    ))}
                  </>
                )}
              </SummonMessage>
            )}
            {loading && <div className="Assistant__spinner"></div>}
          </div>
          <div className="Assistant__chat_spacer" />
        </div>
      </div>
    </BootLoaderShell>
  );
};

export const SettingUpStep = (props: { step: string; details?: string | null; completed: boolean }) => {
  return (
    <div className="Assistant__setting-up-step">
      <div className="Assistant__setting-up-step-row">
        <div className="Assistant__setting-up-step-icon">
          {props.completed ? <div className="Assistant__checkmark-icon" /> : <div className="Assistant__spinner"></div>}
        </div>
        <div className="Assistant__setting-up-step-content">
          <div className="Assistant__setting-up-step-text">
            {props.step}
            {!props.completed && <span className="Assistant__ellipsis"></span>}
          </div>
          {props.details && <div className="Assistant__setting-up-step-details">{props.details}</div>}
        </div>
      </div>
    </div>
  );
};

const BootLoaderShell = (props: { children: React.ReactNode; showLogo?: boolean }) => {
  const [css, $theme] = useStyletron();
  return (
    <div className="GadgetBootLoader">
      <div className="GadgetBootLoader__header">
        {props.showLogo && (
          <div className="GadgetBootLoader__header_logo">
            <GadgetLogoMark />
          </div>
        )}
      </div>
      <div className="GadgetBootLoader__container">{props.children}</div>
    </div>
  );
};
